import React, { useState, useEffect } from 'react';
import { onSnapshot, collection, query, where } from 'firebase/firestore';
import { db } from '../../Services/Firebase/FirebaseIndex';
import debug from '../../utils/debug';

/**
 * Animated Bell Icon Component
 *
 * Features:
 * - Jingles side-to-side when new notifications arrive
 * - Flashes golden on new notification
 * - Gentle pulsing glow for unread notifications
 * - Returns to normal state when all notifications are read
 *
 * @param {string} userId - Current user's UID
 * @param {string} userType - "talent" or "organiser"
 * @param {string} iconSrc - Path to bell icon image
 * @param {string} className - CSS class for styling
 */
const AnimatedBellIcon = ({ userId, userType, iconSrc, className = "menu-icon" }) => {
  const [hasUnread, setHasUnread] = useState(false);
  const [justReceivedNew, setJustReceivedNew] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (!userId) return;

    const cleanupFunctions = [];

    // Listen to UserNotifications collection
    const userNotificationsQuery = query(
      collection(db, 'UserNotifications'),
      where('userId', '==', userId),
      where('userType', '==', userType),
      where('isRead', '==', false)
    );

    const unsubscribeUserNotifications = onSnapshot(
      userNotificationsQuery,
      (snapshot) => {
        const unreadUserNotifs = snapshot.size;

        // Check if count increased (new notification arrived)
        if (unreadUserNotifs > unreadCount && unreadCount > 0) {
          debug.log('AnimatedBellIcon', '🔔 NEW NOTIFICATION ARRIVED!', {
            previous: unreadCount,
            current: unreadUserNotifs
          });
          triggerJingleAnimation();
        }

        setUnreadCount(unreadUserNotifs);
        setHasUnread(unreadUserNotifs > 0);
      },
      (error) => {
        debug.error('AnimatedBellIcon', 'Error listening to UserNotifications', error);
      }
    );

    cleanupFunctions.push(unsubscribeUserNotifications);

    // Also listen to systemNotifications
    const systemNotificationsQuery = query(
      collection(db, 'systemNotifications'),
      where('recipientId', '==', userId),
      where('userType', '==', userType),
      where('isRead', '==', false)
    );

    const unsubscribeSystemNotifications = onSnapshot(
      systemNotificationsQuery,
      (snapshot) => {
        const unreadSystemNotifs = snapshot.size;
        const totalUnread = unreadCount + unreadSystemNotifs;

        // Trigger animation if new notification detected
        if (totalUnread > (unreadCount + unreadSystemNotifs) && unreadCount > 0) {
          triggerJingleAnimation();
        }

        setHasUnread(totalUnread > 0);
      },
      (error) => {
        debug.error('AnimatedBellIcon', 'Error listening to systemNotifications', error);
      }
    );

    cleanupFunctions.push(unsubscribeSystemNotifications);

    return () => {
      cleanupFunctions.forEach(cleanup => cleanup());
    };
  }, [userId, userType, unreadCount]);

  // Trigger jingle animation
  const triggerJingleAnimation = () => {
    setJustReceivedNew(true);

    // Reset animation state after animation completes
    setTimeout(() => {
      setJustReceivedNew(false);
    }, 1500); // Animation duration
  };

  return (
    <>
      <style>
        {`
          /* Bell jingle animation - swings side to side */
          @keyframes bellJingle {
            0%, 100% {
              transform: rotate(0deg);
            }
            10%, 30%, 50%, 70%, 90% {
              transform: rotate(-15deg);
            }
            20%, 40%, 60%, 80% {
              transform: rotate(15deg);
            }
          }

          /* Golden flash on new notification */
          @keyframes goldenFlash {
            0%, 100% {
              filter: brightness(1) drop-shadow(0 0 0 rgba(255, 215, 0, 0));
            }
            20% {
              filter: brightness(2) drop-shadow(0 0 8px rgba(255, 215, 0, 0.9));
            }
            40% {
              filter: brightness(1.5) drop-shadow(0 0 6px rgba(255, 215, 0, 0.7));
            }
            60% {
              filter: brightness(2.5) drop-shadow(0 0 10px rgba(255, 215, 0, 1));
            }
            80% {
              filter: brightness(1.3) drop-shadow(0 0 5px rgba(255, 215, 0, 0.6));
            }
          }

          /* Gentle pulse for unread state */
          @keyframes gentlePulse {
            0%, 100% {
              filter: brightness(1.2) drop-shadow(0 0 4px rgba(255, 215, 0, 0.5));
            }
            50% {
              filter: brightness(1.5) drop-shadow(0 0 8px rgba(255, 215, 0, 0.8));
            }
          }

          /* Subtle glow ring around bell */
          @keyframes glowRing {
            0%, 100% {
              box-shadow: 0 0 0 0 rgba(255, 215, 0, 0.7);
            }
            50% {
              box-shadow: 0 0 0 8px rgba(255, 215, 0, 0);
            }
          }

          /* Bell icon base styles */
          .animated-bell-icon {
            position: relative;
            display: inline-block;
            transform-origin: center top;
          }

          /* Jingle state - active animation */
          .animated-bell-icon.jingle {
            animation: bellJingle 1s ease-in-out, goldenFlash 1.5s ease-out;
          }

          /* Unread state - gentle pulse */
          .animated-bell-icon.unread {
            animation: gentlePulse 2s ease-in-out infinite;
          }

          /* Glow ring for unread */
          .animated-bell-icon.unread::after {
            content: '';
            position: absolute;
            top: 50%;
            left: 50%;
            width: 100%;
            height: 100%;
            transform: translate(-50%, -50%);
            border-radius: 50%;
            animation: glowRing 2s ease-in-out infinite;
            pointer-events: none;
          }

          /* Unread count badge */
          .bell-unread-badge {
            position: absolute;
            top: -4px;
            right: -6px;
            background: linear-gradient(135deg, #F85008 0%, #FFD700 100%);
            color: white;
            font-size: 0.65rem;
            font-weight: bold;
            font-family: 'Montserrat', sans-serif;
            padding: 2px 5px;
            border-radius: 10px;
            min-width: 16px;
            text-align: center;
            box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
            z-index: 10;
            animation: gentlePulse 2s ease-in-out infinite;
          }
        `}
      </style>

      <div
        className={`animated-bell-icon ${className} ${
          justReceivedNew ? 'jingle' : hasUnread ? 'unread' : ''
        }`}
        style={{
          position: 'relative',
          display: 'inline-block'
        }}
      >
        <img
          src={iconSrc}
          alt="Notifications"
          className={className}
          style={{
            display: 'block'
          }}
        />

        {/* Unread count badge */}
        {hasUnread && unreadCount > 0 && (
          <span className="bell-unread-badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </div>
    </>
  );
};

export default AnimatedBellIcon;
